
import * as THREE from "../../libs/three.js/build/three.module.js";
import earcut from "earcut";
import {Utils} from "../utils.js";
import {Line2} from "../../libs/three.js/lines/Line2.js";
import {LineGeometry} from "../../libs/three.js/lines/LineGeometry.js";
import {LineMaterial} from "../../libs/three.js/lines/LineMaterial.js";
import {createStyledLabel, LABEL_RENDER_ORDER} from "./DigitizationStyle.js";
import {computeScreenSpaceScale, ScreenSpaceSizes} from "./ScreenSpaceScale.js";
import {resolveVertexRole, vertexRoleStyle, VERTEX_ROLE} from "./vertexSemantics.js";
import {projectToScreen} from "./geometry/polylineGeometry.js";
import {planDirectionMarkers} from "./geometry/lineDirection.js";
import {showHoverTooltip, hideHoverTooltip} from "./HoverTooltip.js";
import {createCopyIconMarker, wireCopyIcon, wireCopyIconText, positionCopyIconAtLabelCorner} from "./CopyIconMarker.js";
import {bumpLabelRevision, setLabelWanted, setLabelContent, setLabelLadder, setLabelAnchor, setLabelRole, applyLabelOffset, LABEL_ROLE_RESULT} from "./labels/labelState.js";
import {freezeHover, unfreezeHover, hoverEnter, hoverLeave} from "./labels/hoverState.js";

// Plads (canvas-px) en kompakt, enkeltlinjet label afsætter til kopi-ikonet i sit hjørne, så ikonet ikke dækker teksten
const COPY_ICON_PAD = 26;

import {t} from "./i18n/index.js";
const COLOR_DEFAULT = new THREE.Color(0x2f8fd6);
const COLOR_SELECTED = new THREE.Color(0xffa500);
// Selection/highlight audit finding: this must be a DIFFERENT value from
// COLOR_SELECTED - see setVertexHighlight()'s own doc comment below for why
// (previously VectorEditingMenu.js's segment/range highlight reused
// COLOR_SELECTED's exact hex value, making a highlighted segment on an
// already-selected feature visually indistinguishable from every other
// segment of that same feature). Matches VectorEditingMenu.js's existing
// PREVIEW_COLOR (0xffdc00, "this is the interactive target") - reusing an
// already-established colour rather than inventing a new design system.
const COLOR_HIGHLIGHT_HEX = 0xffdc00;

// One shared primitive per visual vertex ROLE (see vertexSemantics.js) - a
// FeatureVisual swaps a sphere's `.geometry` between these to render the
// ●/■/◎ hierarchy, it never builds per-vertex geometry of its own. Module-
// level and never disposed (three tiny buffers, shared across every visual in
// the scene - same lifetime convention as any other shared constant here);
// each sphere still owns its own MATERIAL, which is what selection/hover/
// highlight mutate. All built at unit size so ScreenSpaceScale's pixel
// targets stay literal.
//   VERTEX  -> low-detail sphere (round, least dominant)
//   ENDPOINT-> cube, camera-billboarded to a crisp square face
//   POINT_FEATURE -> ring (torus), camera-billboarded, most prominent
const ROLE_GEOMETRY = {
	[VERTEX_ROLE.VERTEX]: new THREE.SphereGeometry(1, 16, 16),
	[VERTEX_ROLE.ENDPOINT]: new THREE.BoxGeometry(1.7, 1.7, 1.7),
	[VERTEX_ROLE.POINT_FEATURE]: new THREE.TorusGeometry(0.95, 0.34, 12, 28),
};

// Forms that must face the camera every frame to read as intended (a square
// face for the cube, a filled ring for the torus). A plain sphere looks the
// same from any angle, so it is deliberately absent here.
const BILLBOARDED_ROLES = new Set([VERTEX_ROLE.ENDPOINT, VERTEX_ROLE.POINT_FEATURE]);

function createEdge(){
	const geometry = new LineGeometry();
	geometry.setPositions([0, 0, 0, 0, 0, 0]);

	const material = new LineMaterial({
		color: COLOR_DEFAULT.getHex(),
		linewidth: 2,
		resolution: new THREE.Vector2(1000, 1000),
	});
	material.depthTest = false;

	const edge = new Line2(geometry, material);
	edge.computeLineDistances();
	// same "must draw over a Polygon's own fill" fix as labels/copy-icons -
	// see DigitizationStyle.js's own doc comment on LABEL_RENDER_ORDER
	edge.renderOrder = LABEL_RENDER_ORDER;

	return edge;
}

// screen-space proximity, in pixels, within which a segment's "insert
// vertex here" handle reveals itself (see updateInsertHandles())
const INSERT_HANDLE_REVEAL_PX = 18;

// Small "+" badge marking a segment's midpoint, hidden until the cursor
// comes near it (see updateInsertHandles()) - clicking it splits that
// segment by inserting a real vertex there. Styled to match the project's
// standing label convention (light-gray 50% background, white glyph).
function createPlusMarker(){
	const size = 64;
	const canvas = document.createElement('canvas');
	canvas.width = size;
	canvas.height = size;
	const ctx = canvas.getContext('2d');

	ctx.beginPath();
	ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2);
	ctx.fillStyle = 'rgba(211,211,211,0.5)';
	ctx.fill();

	ctx.strokeStyle = '#fff';
	ctx.lineWidth = 6;
	ctx.lineCap = 'round';
	const pad = size * 0.28;
	ctx.beginPath();
	ctx.moveTo(size / 2, pad);
	ctx.lineTo(size / 2, size - pad);
	ctx.moveTo(pad, size / 2);
	ctx.lineTo(size - pad, size / 2);
	ctx.stroke();

	const texture = new THREE.CanvasTexture(canvas);
	// Deliberately a Mesh, not a Sprite: Potree's InputHandler picking does
	// not raycast-pick Sprites (confirmed - a Sprite-based handle never
	// received a real click), while Mesh objects are the same proven-pickable
	// primitive the vertex spheres already use. Billboarded manually every
	// frame (see updateInsertHandles) to face the camera like a sprite would.
	const material = new THREE.MeshBasicMaterial({map: texture, depthTest: false, depthWrite: false, transparent: true});
	const geometry = new THREE.PlaneGeometry(1, 1);
	const mesh = new THREE.Mesh(geometry, material);
	mesh.visible = false;
	mesh.renderOrder = LABEL_RENDER_ORDER; // same "must draw over a Polygon's own fill" fix - see DigitizationStyle.js

	// belt-and-braces alongside the .visible check: while hidden, raycasting
	// (picking) is a no-op too, so a stale/off-proximity handle can never
	// register a click no matter how InputHandler treats invisible objects
	mesh.userData.baseRaycast = mesh.raycast.bind(mesh);
	mesh.raycast = () => {};

	return mesh;
}

function setInsertHandleNear(handle, near){
	handle.visible = near;
	handle.raycast = near ? handle.userData.baseRaycast : (() => {});
}

// A single "›" chevron, drawn once on a canvas and reused for every direction
// marker of every line (one shared geometry + texture, module-level). The
// glyph points toward +local-X so the caller only has to billboard the mesh
// and spin it about the view axis to the segment's on-screen heading. Pure
// outline (two strokes, round caps) - deliberately unlike a filled vertex
// sphere, a cube endpoint, a ring Point-feature, the "+" insert handle or a
// selection handle, so it never reads as any of those. Never raycast-picked.
const CHEVRON_GEOMETRY = new THREE.PlaneGeometry(1, 1);
let _chevronTexture = null;
function chevronTexture(){
	if(_chevronTexture) return _chevronTexture;
	const size = 64;
	const canvas = document.createElement('canvas');
	canvas.width = size;
	canvas.height = size;
	const ctx = canvas.getContext('2d');
	ctx.strokeStyle = '#fff';
	ctx.lineWidth = 9;
	ctx.lineCap = 'round';
	ctx.lineJoin = 'round';
	const x0 = size * 0.34, x1 = size * 0.66;
	ctx.beginPath();
	ctx.moveTo(x0, size * 0.22);
	ctx.lineTo(x1, size * 0.5);
	ctx.lineTo(x0, size * 0.78);
	ctx.stroke();
	_chevronTexture = new THREE.CanvasTexture(canvas);
	return _chevronTexture;
}

function createChevronMarker(){
	const material = new THREE.MeshBasicMaterial({
		map: chevronTexture(),
		color: COLOR_DEFAULT.getHex(),
		transparent: true,
		opacity: 0.5,
		depthTest: false,
		depthWrite: false,
	});
	const mesh = new THREE.Mesh(CHEVRON_GEOMETRY, material);
	mesh.visible = false;
	mesh.raycast = () => {}; // decorative only
	mesh.renderOrder = LABEL_RENDER_ORDER; // same "must draw over a Polygon's own fill" fix - see DigitizationStyle.js
	return mesh;
}

function disposeChevron(mesh){
	// geometry + texture are module-level shared - only the per-marker
	// material is this visual's to free
	mesh.material.dispose();
}

// GPU-resource teardown helpers, paired with each object's own creation
// site above (createEdge/createPlusMarker/createCopyIconMarker/
// createStyledLabel). Every one of `_rebuildEdges()`/`removeVertex()`/
// `setPoints()`/`_rebuildSegmentLabels()`/`_rebuildInsertHandles()` used to
// only call `this.remove(...)` (scene-graph detach) without ever calling
// `.dispose()` on the discarded geometry/material/texture - a real,
// reachable leak (not theoretical: `_rebuildEdges()` alone reruns on every
// single `addMarker()` during capture, so digitizing an N-vertex polyline
// already leaked N-1 discarded LineGeometry/LineMaterial pairs before this
// fix). Vertex-marker geometry is the one exception - the module-level shared
// ROLE_GEOMETRY set (see vertexSemantics.js), never per-visual, so only each
// sphere's own MATERIAL is disposed, never its (shared) geometry.
function disposeSphere(sphere){
	sphere.material.dispose();
}

function disposeLabel(label){
	if(!label) return;
	// frigiver materialet og alle label'ens cachede teksturer (ikke kun den nuværende)
	if(typeof label.dispose === "function"){
		label.dispose();
	}else{
		label.material.dispose();
		label.texture.dispose();
	}
	if(label.userData) label.userData.pvRec = null;
	bumpLabelRevision();
}

function disposeCopyIcon(icon){
	if(!icon) return;
	icon.geometry.dispose();
	icon.material.map.dispose();
	icon.material.dispose();
}

function disposeEdge(edge){
	edge.geometry.dispose();
	edge.material.dispose();
}

function disposeInsertHandle(handle){
	handle.geometry.dispose();
	handle.material.map.dispose();
	handle.material.dispose();
}

// Generic fallback for subtrees this class doesn't build itself but still
// ends up owning as a plain child (e.g. "Areal og volumen"'s QA visual -
// see volumeTool.js's `visual.add(qaBuild.group); visual.volumeQaGroup = ...`,
// a different, ad-hoc convention from the `userData.auxiliaryVisual` hook
// used elsewhere, kept as-is here since `FeaturePanel.js` also reads
// `visual.volumeQaGroup` directly - not worth changing that contract just
// for this). Walks every descendant and frees whatever geometry/material
// (and material.map texture, if any) it finds.
function disposeObjectTree(root){
	root.traverse((obj) => {
		if(obj.geometry) obj.geometry.dispose();
		if(obj.material){
			if(obj.material.map) obj.material.map.dispose();
			obj.material.dispose();
		}
	});
}

// One line per axis (rather than "X: .. Y: .. Z: .." on a single line) so
// the label stays narrow/compact instead of growing wide with long UTM
// coordinates - user-requested formatting, applies to every vertex/computed-
// point label via this one shared formatter.
// exported so the host app's GCP labels use the very same coordinate lines
export function formatCoordinateLine(position){
	const x = Utils.addCommas(position.x.toFixed(3));
	const y = Utils.addCommas(position.y.toFixed(3));
	const z = Utils.addCommas(position.z.toFixed(3));
	return `X: ${x}\nY: ${y}\nZ: ${z}`;
}

// Generic THREE.js representation of a digitized Point/LineString/Polygon
// feature: draggable vertex spheres + connecting edges, each vertex carrying
// its own 3-line label (Name / Description / coordinates). Modeled closely on
// the existing Measure.js/Profile.js marker mechanics so it plugs into
// Potree's InputHandler picking/dragging for free.
//
// This class is purely visual/interactive. The actual registered data lives
// in a separate Feature object (see Feature.js/FeatureManager.js); this class
// only reports geometry changes via the 'geometry_changed' event.
export class FeatureVisual extends THREE.Object3D {
	constructor(geometryType){
		super();

		this.constructor.counter = (this.constructor.counter === undefined) ? 0 : this.constructor.counter + 1;
		this.name = "";
		this.description = "";

		this.geometryType = geometryType; // 'Point' | 'LineString' | 'Polygon'
		this.closed = geometryType === "Polygon";
		this.editable = true;
		this.minVertices = geometryType === "Polygon" ? 3 : (geometryType === "Point" ? 1 : 2);

		// set by DigitizationModule when a tool's captured points are not the
		// feature's final geometry (e.g. the Midpoint tool captures 2 points
		// but stores their calculated midpoint)
		this.postProcessCoordinates = null;

		// optional per-vertex role names (e.g. ["Startpunkt", "Slutpunkt"])
		// shown on each vertex's label instead of the generic feature name -
		// used by calculated-geometry tools where the captured points aren't
		// the feature itself, only inputs to it
		this.vertexRoleNames = null;

		// optional non-interactive "ghost" point derived from the captured
		// points (e.g. the Midpoint tool's calculated midpoint): never part
		// of `points`/`spheres`, so it is never draggable and never counted
		// as a captured vertex. See enableComputedPoint().
		this.computedPointName = null;
		this.computeFn = null;
		this.computedMarker = null;
		this.computedLabel = null;
		this.computedCopyIcon = null;

		// A tool-provided STATIC result label (e.g. "Areal og volumen"'s
		// compact Areal/Påfyldning/Udgravning summary) - distinct from
		// computedPoint (a DERIVED marker+label pair, recomputed from the
		// captured points via a formula, used by tools like Midpoint). A
		// result label has no marker shape and its text is set directly by
		// the tool via setResultLabel() - never recomputed here - but still
		// shares the same createStyledLabel() styling and the same
		// labelsVisible/updateLabelScale() machinery as every other label.
		this.resultLabel = null;
		// UI audit finding: every other label type (vertex, computed point)
		// already carries a copy icon; resultLabel did not, so a tool like
		// "Areal og volumen" had no way to copy its shown result at all -
		// same wireCopyIconText() pattern PipeVisual.js's own result label
		// already uses (copies the rendered lines, not a coordinate, since a
		// result label shows computed values, not a single point).
		this.resultCopyIcon = null;
		this._resultLabelLines = null;

		// per-object label visibility (section 5/6/9 of the labels spec): OFF
		// by default for a new feature (user-requested: ordinary labels
		// shouldn't clutter the viewer by default), independent of feature/
		// geometry visibility - see setLabelsVisible(). Still fully togglable
		// per-visual (Registreringer's own checkbox) and globally (the "L"
		// keyboard shortcut - see DigitizationModule.js) exactly as before;
		// only the STARTING state changed. Belongs to this visual, not a
		// global viewer setting, so different features can still differ once
		// toggled individually.
		this.labelsVisible = false;

		// Consolidation audit finding (task: "to forskellige label-klasser"):
		// resultLabel used to be slaved 1:1 to labelsVisible (the same "blue"
		// toggle governing vertex/segment/summary-format labels) - toggling
		// the ordinary labels off also hid the result label, and there was no
		// way to hide just one without the other. A SEPARATE flag, mirroring
		// labelsVisible's own contract exactly (ON by default, per-visual, not
		// a global setting) - see setResultLabelVisible()/setResultLabel().
		this.resultLabelVisible = true;

		// Independent of labelsVisible: lets a tool hide just the per-vertex
		// coordinate labels (e.g. once a computed result label takes over as
		// the primary on-screen information) without affecting the feature's
		// own labelsVisible checkbox state - see setVertexLabelsSuppressed().
		// OFF by default so every existing tool's behaviour is unchanged.
		this.vertexLabelsSuppressed = false;

		this.points = [];
		this.spheres = [];
		this.edges = [];
		this.vertexLabels = [];
		this.copyIcons = [];
		this.insertHandles = [];

		// Reusable pool of "›" direction chevrons (see updateDirectionIndicators()).
		// Grown on demand, hidden when unused. Only ever populated for
		// registered open LineString features - DigitizationModule.update()
		// decides that and simply doesn't call updateDirectionIndicators() for
		// anything else (Polygon rings, construction/helper lines, Rør).
		this.directionMarkers = [];

		// Målinger tools (Mål afstand/areal) opt-in support: one label per
		// EDGE (not vertex) showing that segment's own length, and one
		// auto-recomputed summary label (reusing resultLabel's own existing
		// mechanism/styling - see enableSummaryLabel()). Both null/off by
		// default so every existing tool is unaffected; see
		// enableSegmentLabels()/enableSummaryLabel().
		this.segmentLabelFormat = null;
		this.segmentLabels = [];
		this.summaryLabelFormat = null;
		this.summaryLabelAnchor = null;

		// Polygon fill opt-in (Mål areal / Polygon - see enableFill()). Off by
		// default, same reasoning as the fields above.
		this.fillColor = null;
		this.fillOpacity = 0.35;
		this.fillMesh = null;

		// per-vertex marker geometry is chosen by visual role, from the shared
		// ROLE_GEOMETRY set (see vertexSemantics.js / _refreshVertexStyles()) -
		// all built at world-space radius 1 so ScreenSpaceScale's targetPixels
		// values are literal, precise on-screen pixel sizes

		this.selected = false;
		// LabelManager (labels/LabelManager.js) genkender FeatureVisuals på dette flag (også underklasser)
		this.isFeatureVisual = true;
	}

	// registers a derived/"ghost" point rendered on top of the captured
	// geometry (distinct shape+color, never draggable) whose position is
	// recomputed from rawCoordinates() every time the captured points change.
	// computeFn(rawPoints) must return [x,y,z] or null/undefined when there
	// isn't enough input yet.
	enableComputedPoint(roleName, computeFn){
		this.computedPointName = roleName;
		this.computeFn = computeFn;

		if(!this.computedMarker){
			const geometry = new THREE.SphereGeometry(1, 16, 16); // kugle (var oktaeder/"pyramide")
			const material = new THREE.MeshLambertMaterial({
				color: 0xffdc00,
				depthTest: false,
				depthWrite: false,
			});
			this.computedMarker = new THREE.Mesh(geometry, material);
			this.computedMarker.visible = false;
			this.computedMarker.renderOrder = LABEL_RENDER_ORDER; // same "must draw over a Polygon's own fill" fix - see DigitizationStyle.js
			this.add(this.computedMarker);

			this.computedLabel = createStyledLabel("");
			this.computedLabel.visible = false;
			this.add(this.computedLabel);

			this.computedCopyIcon = createCopyIconMarker();
			wireCopyIcon(this.computedCopyIcon, () => this.computedMarker.visible ? this.computedMarker.position : null);
			this.add(this.computedCopyIcon);
		}

		this._refreshComputedPoint();
	}

	// Målinger tools opt-in: one label per edge showing that segment's own
	// value (e.g. its length) - formatFn(posA, posB, segmentIndex) => string.
	// Rebuilt whenever the edge count changes (same points as
	// _rebuildEdges()) and repositioned/retexted every _sync() (drag, vertex
	// add/remove), so it stays live exactly like every other derived label
	// here - task's own "værdierne opdateres dynamisk hvis vertex flyttes".
	// Visibility follows labelsVisible, independent of vertexLabelsSuppressed
	// (a segment label is not a vertex coordinate label).
	enableSegmentLabels(formatFn){
		this.segmentLabelFormat = formatFn;
		this._rebuildSegmentLabels();
		this._layoutSegmentLabels();
	}

	// Målinger tools opt-in: an auto-recomputed variant of the existing
	// resultLabel mechanism (setResultLabel()) - formatFn(rawCoordinates,
	// visual) => array of lines, or null/undefined to hide the label (e.g.
	// before enough points are captured); anchorFn(rawCoordinates, visual)
	// => {x,y,z} world position for the label. Both receive `visual` (this)
	// as their second argument - not just the tool DEFINITION's own static
	// formatFn/anchorFn closure, since a feature's own name changes over its
	// lifetime (the generic tool name during capture, e.g. "Mål afstand",
	// then the real numbered feature name, e.g. "Mål afstand 1", once
	// capture finishes and setInfo() is called with it - see
	// DigitizationModule.js's buildFeatureIfValid) and the summary line
	// needs whichever is current at the time it re-renders, not whatever
	// was true when enableSummaryLabel() was first called.
	//
	// Re-evaluated every _sync(), unlike setResultLabel() itself (a one-shot
	// call other tools like Areal og volumen use once after a Worker
	// computation finishes) - this is the generic "keep recomputing from
	// whatever the user has drawn so far" variant three Målinger tools all
	// need, so it lives here once rather than each tool re-listening to
	// geometry_changed and reimplementing the same recompute-and-call-
	// setResultLabel dance.
	enableSummaryLabel(formatFn, anchorFn){
		this.summaryLabelFormat = formatFn;
		this.summaryLabelAnchor = anchorFn;
		this._refreshSummaryLabel();
	}

	// Semi-transparent fill for a Polygon-type visual (Mål areal / Polygon -
	// user-requested green shading). Fixed color/opacity, not selection-
	// reactive - rebuilt from scratch on every _sync() (drag, add/remove
	// vertex), since re-triangulating a small polygon via earcut is cheap and
	// always correct, unlike trying to incrementally patch a triangle fan.
	enableFill(color, opacity){
		this.fillColor = color;
		if(opacity !== undefined && opacity !== null) this.fillOpacity = opacity;
		this._rebuildFill();
	}

	createSphereMaterial(){
		return new THREE.MeshLambertMaterial({
			// .getHex() (a plain number), not the shared Color instance
			// itself - see setSelected()'s own doc comment for why passing
			// the instance directly must be avoided
			color: (this.selected ? COLOR_SELECTED : COLOR_DEFAULT).getHex(),
			depthTest: false,
			depthWrite: false,
		});
	}

	// updates the Name/Description shown on every vertex's label (e.g. once a
	// tool finishes capturing and the feature gets its real, numbered name;
	// or when the user edits attributes afterwards)
	setInfo(name, description){
		this.name = name || "";
		this.description = description || "";

		for(let i = 0; i < this.vertexLabels.length; i++){
			this._refreshVertexLabel(i);
		}
		this._refreshComputedPoint();
		// A Målinger tool's summary label shows the feature's own name (see
		// enableSummaryLabel()'s doc) - without this, the label would keep
		// showing the generic tool name (e.g. "Mål afstand") until the next
		// unrelated geometry edit happened to trigger a re-render, even
		// though setInfo() is exactly the call that gives the feature its
		// real, numbered name once capture finishes.
		this._refreshSummaryLabel();
	}

	setSelected(value){
		this.selected = value;
		bumpLabelRevision();

		// Audit finding: assigning the shared COLOR_SELECTED/COLOR_DEFAULT
		// Color INSTANCE directly (`.material.color = color`) makes every
		// sphere/edge material alias that one shared object. Any later
		// in-place mutation of a material's own .color (e.g.
		// VectorEditingMenu.js's highlightRange()/clearHighlight() calling
		// .setHex() on it) would then silently corrupt the shared
		// COLOR_SELECTED/COLOR_DEFAULT singleton for every OTHER feature in
		// the whole scene that has ever been selected/deselected too - not
		// just this one. .copy() copies the RGB values into each material's
		// own independent Color object instead, exactly like
		// createSphereMaterial()'s constructor-based path already does via
		// .getHex().
		const color = value ? COLOR_SELECTED : COLOR_DEFAULT;
		for(const sphere of this.spheres) sphere.material.color.copy(color);
		for(const edge of this.edges) edge.material.color.copy(color);
	}

	// Selection/highlight audit finding: opening a vertex-menu (Shift+Right-
	// click on a vertex) previously left NO persistent visual indication of
	// which vertex the menu concerns beyond the native hover emissive (see
	// _wireSphere()'s mouseover/mouseleave) - which disappears the instant
	// the cursor moves off the sphere toward the menu itself, even though
	// the menu is still open and about to act on that exact vertex.
	// `on: true` paints just that one sphere the same COLOR_HIGHLIGHT_HEX
	// used by VectorEditingMenu.js's segment/range highlight (a single,
	// consistent "this is the current operation's target" colour, distinct
	// from both COLOR_DEFAULT and COLOR_SELECTED); `on: false` restores it
	// to whatever this feature's current whole-object selected state is -
	// never a hardcoded default, so restoring a highlighted vertex on an
	// already-selected feature correctly leaves it orange, not blue.
	setVertexHighlight(index, on){
		const sphere = this.spheres[index];
		if(!sphere) return;

		if(on){
			sphere.material.color.setHex(COLOR_HIGHLIGHT_HEX);
		}else{
			sphere.material.color.copy(this.selected ? COLOR_SELECTED : COLOR_DEFAULT);
		}
	}

	// Assigns each vertex sphere the shared geometry for its current visual
	// role (see vertexSemantics.js) - ordinary vertex vs. LineString endpoint
	// vs. standalone Point-feature. Endpoint-ness depends on a vertex's index
	// AND on the total count, so this must re-run after every structural
	// change (addMarker/removeVertex/setPoints), not just on creation - a
	// vertex that was the last one stops being an endpoint the moment another
	// is appended after it. Only `.geometry` and `userData.vertexRole` change;
	// the material (and thus selection/hover/highlight colour state) is left
	// exactly as it was. The per-frame on-screen SIZE for each role is applied
	// separately in updateSphereScale().
	_refreshVertexStyles(){
		const count = this.points.length;
		for(let i = 0; i < this.spheres.length; i++){
			const role = resolveVertexRole(this.geometryType, i, count);
			const sphere = this.spheres[i];
			sphere.userData.vertexRole = role;
			const geometry = ROLE_GEOMETRY[role] || ROLE_GEOMETRY[VERTEX_ROLE.VERTEX];
			if(sphere.geometry !== geometry) sphere.geometry = geometry;
		}
	}

	addMarker(position, index = null){
		const point = {position: position.clone ? position.clone() : new THREE.Vector3(...position)};
		const insertIndex = (index === null) ? this.points.length : index;
		this.points.splice(insertIndex, 0, point);

		const sphere = new THREE.Mesh(ROLE_GEOMETRY[VERTEX_ROLE.VERTEX], this.createSphereMaterial());
		sphere.featureVisual = this;
		// same "must draw over a Polygon's own fill" fix as labels/copy-icons -
		// see DigitizationStyle.js's own doc comment on LABEL_RENDER_ORDER
		sphere.renderOrder = LABEL_RENDER_ORDER;
		this.spheres.splice(insertIndex, 0, sphere);
		this.add(sphere);

		const label = createStyledLabel("");
		this.vertexLabels.splice(insertIndex, 0, label);
		this.add(label);

		const copyIcon = createCopyIconMarker();
		this._wireCopyIcon(copyIcon);
		this.copyIcons.splice(insertIndex, 0, copyIcon);
		this.add(copyIcon);

		this._wireSphere(sphere);
		this._rebuildEdges();
		this._rebuildSegmentLabels();
		this._rebuildInsertHandles();
		this._refreshVertexStyles();
		this._sync();

		this.dispatchEvent({type: "vertex_added", index: insertIndex});

		return sphere;
	}

	removeVertex(index){
		if(this.points.length <= this.minVertices){
			return false;
		}

		this.points.splice(index, 1);
		this.remove(this.spheres[index]);
		disposeSphere(this.spheres[index]);
		this.spheres.splice(index, 1);
		this.remove(this.vertexLabels[index]);
		disposeLabel(this.vertexLabels[index]);
		this.vertexLabels.splice(index, 1);
		this.remove(this.copyIcons[index]);
		disposeCopyIcon(this.copyIcons[index]);
		this.copyIcons.splice(index, 1);

		this._rebuildEdges();
		this._rebuildSegmentLabels();
		this._rebuildInsertHandles();
		this._refreshVertexStyles();
		this._sync();

		this.dispatchEvent({type: "vertex_removed", index});

		return true;
	}

	appendAtEnd(position){
		return this.addMarker(position, this.points.length);
	}

	// Central, safe whole-geometry replacement - the mechanism every Vektor-
	// operation (Ret ud, Forenkle, Ortogonaliser, Fast vinkel, Parallel/
	// vinkelret, Indsæt punkt, Vend retning - see tools/vectorEditingMenu.js)
	// goes through, rather than mutating `points`/`spheres` directly in
	// several places. Handles a vertex COUNT change (Forenkle removes
	// vertices, Indsæt punkt/segment-split adds one) as well as a same-count
	// reposition (Ret ud, Ortogonaliser, ...): tears down every existing
	// per-vertex sphere/label/copy-icon and rebuilds via addMarker(), the
	// same construction path a fresh capture already uses, so there is only
	// ever one place that wires a vertex's interactive bits correctly - not
	// a second, parallel one for "editing an existing feature in place".
	//
	// `newPositions` must have at least minVertices entries (task: no
	// operation may leave a feature with fewer than its geometry type's
	// minimum). Returns false and does nothing otherwise.
	//
	// vertexRoleNames (per-vertex label text some calculated tools set, e.g.
	// Midterlinje's "Midtpunkt 1/2/...") is reset to null whenever the
	// vertex COUNT changes - a stale role-name array indexed against the old
	// count would silently mislabel or drop labels; when the count is
	// unchanged (a pure reposition), the existing role names are still
	// valid at their same indices and are left alone.
	setPoints(newPositions){
		if(newPositions.length < this.minVertices) return false;

		if(this.vertexRoleNames && this.vertexRoleNames.length !== newPositions.length){
			this.vertexRoleNames = null;
		}

		for(const s of this.spheres){ this.remove(s); disposeSphere(s); }
		for(const l of this.vertexLabels){ this.remove(l); disposeLabel(l); }
		for(const c of this.copyIcons){ this.remove(c); disposeCopyIcon(c); }
		this.points = [];
		this.spheres = [];
		this.vertexLabels = [];
		this.copyIcons = [];

		for(const pos of newPositions){
			const point = {position: pos.clone ? pos.clone() : new THREE.Vector3(pos.x, pos.y, pos.z)};
			this.points.push(point);

			const sphere = new THREE.Mesh(ROLE_GEOMETRY[VERTEX_ROLE.VERTEX], this.createSphereMaterial());
			sphere.featureVisual = this;
			sphere.renderOrder = LABEL_RENDER_ORDER;
			this.spheres.push(sphere);
			this.add(sphere);

			const label = createStyledLabel("");
			this.vertexLabels.push(label);
			this.add(label);

			const copyIcon = createCopyIconMarker();
			this._wireCopyIcon(copyIcon);
			this.copyIcons.push(copyIcon);
			this.add(copyIcon);

			this._wireSphere(sphere);
		}

		this._rebuildEdges();
		this._rebuildSegmentLabels();
		this._rebuildInsertHandles();
		this._refreshVertexStyles();
		this._sync();

		this.dispatchEvent({type: "geometry_replaced"});

		return true;
	}

	// splits segment `segmentIndex` (between points[segmentIndex] and its
	// successor) by inserting a real vertex at its geometric midpoint -
	// invoked by clicking a revealed insert-handle (see updateInsertHandles())
	insertVertexAt(segmentIndex){
		if(!this.editable) return;

		const n = this.points.length;
		const a = this.points[segmentIndex].position;
		const b = this.points[(segmentIndex + 1) % n].position;
		const mid = a.clone().add(b).multiplyScalar(0.5);

		this.addMarker(mid, segmentIndex + 1);
	}

	_wireInsertHandle(handle){
		const click = () => {
			const i = this.insertHandles.indexOf(handle);
			if(i !== -1) this.insertVertexAt(i);
		};

		// InputHandler only ever dispatches 'click' to the object it started
		// dragging on mousedown, and it only starts a drag on an object that
		// has its own 'drag' listener (see InputHandler.onMouseDown/onMouseUp)
		// - so a 'click'-only listener here would silently never fire. The
		// no-op 'drag' handler exists purely to make this object a valid
		// drag/click target; the marker itself must never actually move.
		handle.addEventListener('drag', () => {});
		handle.addEventListener('click', click);
		handle.addEventListener('mouseover', () => {
			showHoverTooltip(t('Indsæt punkt'));
			hoverEnter("handle", this, this.insertHandles.indexOf(handle), performance.now());
		});
		handle.addEventListener('mouseleave', () => {
			hideHoverTooltip();
			hoverLeave("handle", this, this.insertHandles.indexOf(handle), performance.now());
		});
	}

	// looks up its own current vertex index at click time (not a captured
	// closure index) since vertices can be inserted/removed after this icon
	// was created, shifting every index after them
	_wireCopyIcon(icon){
		wireCopyIcon(icon, () => {
			const i = this.copyIcons.indexOf(icon);
			return i !== -1 ? this.points[i].position : null;
		});
	}

	// Shows/hides every label (and their copy-icons) belonging to this
	// feature without touching the geometry (spheres/edges) at all - feature
	// visibility and label visibility are independent (see FeatureVisual
	// constructor's labelsVisible field).
	setLabelsVisible(visible){
		this.labelsVisible = visible;

		for(let i = 0; i < this.points.length; i++) this._refreshVertexLabel(i);
		this._refreshComputedPoint();
		this._layoutSegmentLabels();
		// resultLabel's own visibility is independent (see resultLabelVisible/
		// setResultLabelVisible()) - _refreshSummaryLabel() still runs here to
		// keep its CONTENT current (a geometry change may have happened while
		// labels were hidden), but it must not flip visibility itself; see its
		// own doc comment.
		this._refreshSummaryLabel();

		// Generic, duck-typed extensibility hook (task section 39: only add
		// shared machinery when it's real UI-contract behaviour, not tool-
		// specific logic) - a tool may attach a decorative child object via
		// `visual.userData.auxiliaryVisual = someObject` (see Mål højde's
		// HeightAuxiliaryVisual.js) that has its own setLabelsVisible(); this
		// keeps that object's labels in sync with the SAME Registreringer
		// checkbox that controls this feature's own labels, without
		// FeatureVisual needing to know anything about what that object is.
		if(this.userData.auxiliaryVisual && this.userData.auxiliaryVisual.setLabelsVisible){
			this.userData.auxiliaryVisual.setLabelsVisible(visible);
		}
	}

	// Sets/updates the tool-provided static result label - creates it (via
	// the same createStyledLabel() every other digitization label uses) on
	// first call. `lines`: array of strings, joined with "\n" (matches every
	// other label's multi-line convention). `position`: world-space THREE.Vector3
	// or {x,y,z} - the caller picks the anchor (e.g. "Areal og volumen" uses
	// its own already-computed reference-plane centroid, not a vertex).
	setResultLabel(lines, position){
		if(!this.resultLabel){
			// "result" variant (task: "resultat-label skal have orange border") -
			// visually distinct from every ordinary label by construction, not
			// by a per-call override.
			this.resultLabel = createStyledLabel("", "result");
			setLabelRole(this.resultLabel, LABEL_ROLE_RESULT);
			this.add(this.resultLabel);

			this.resultCopyIcon = createCopyIconMarker();
			wireCopyIconText(this.resultCopyIcon, () => this._resultLabelLines ? this._resultLabelLines.join("\n") : null);
			this.add(this.resultCopyIcon);
		}
		this._resultLabelLines = lines;
		// Resultatet er værktøjets egen sammenhængende helhed: ét trin (alle linjer). LabelManager skjuler det hellere end at reducere det.
		this.resultLabel.userData.pvIconPad = COPY_ICON_PAD;
		setLabelLadder(this.resultLabel, [null, null, null, lines.join("\n")]);
		setLabelAnchor(this.resultLabel, position.x, position.y, position.z);
		setLabelWanted(this.resultLabel, this.resultLabelVisible);
	}

	// Independent visibility control for the result label (task: "uafhængig
	// visibility" - the orange Registreringer toggle, alongside the existing
	// blue setLabelsVisible()). A feature with no result label yet (no tool-
	// provided summary has ever computed successfully) simply has nothing to
	// show/hide - the flag itself still updates, so a result label created
	// later (e.g. once a Linje reaches 2 points) picks up the current
	// preference immediately rather than always starting visible.
	setResultLabelVisible(visible){
		this.resultLabelVisible = visible;
		if(this.resultLabel) setLabelWanted(this.resultLabel, visible);
	}

	// Whether this visual currently has (or could ever have) a result label -
	// used by sidebar.js to decide whether a feature row needs the orange
	// toggle at all (task: "Resultat-toggle skal kun være relevant for
	// objekter, der faktisk har et resultat-label"). True once a summary
	// label has been computed at least once; a tool with no
	// summaryLabelFormat/setResultLabel() call ever configured never sets
	// resultLabel, so this stays false for e.g. a plain Punkt.
	hasResultLabel(){
		return this.resultLabel !== null;
	}

	// Hides (or restores) just the per-vertex coordinate labels, independent
	// of labelsVisible - task section 6/7: after a successful "Areal og
	// volumen" computation, the individual polygon-vertex coordinate labels
	// (X/Y/Z at every corner) would otherwise compete visually with the new
	// result label and the TIN; the polygon outline/vertices themselves stay
	// exactly as visible/editable as before, only their coordinate labels
	// are suppressed.
	setVertexLabelsSuppressed(suppressed){
		this.vertexLabelsSuppressed = suppressed;
		for(let i = 0; i < this.points.length; i++) this._refreshVertexLabel(i);
	}

	_rebuildInsertHandles(){
		for(const handle of this.insertHandles){ this.remove(handle); disposeInsertHandle(handle); }
		this.insertHandles = [];

		const n = this.points.length;
		if(n < 2) return;

		const segmentCount = this.closed ? n : n - 1;
		for(let i = 0; i < segmentCount; i++){
			const handle = createPlusMarker();
			this._wireInsertHandle(handle);
			this.add(handle);
			this.insertHandles.push(handle);
		}
	}

	// Reveals each segment's "+" insert handle only when the cursor is
	// currently within INSERT_HANDLE_REVEAL_PX of that segment's midpoint in
	// screen space - `mouse` is the module's live canvas-relative cursor
	// position, or null while there is none (cursor left the canvas, or
	// digitization is Ctrl-paused for navigation), which hides every handle.
	updateInsertHandles(camera, mouse, clientWidth, clientHeight){
		if(!this.editable || !mouse || this.points.length < 2){
			for(const handle of this.insertHandles) setInsertHandleNear(handle, false);
			return;
		}

		const n = this.points.length;
		const cameraPos = camera.position;

		for(let i = 0; i < this.insertHandles.length; i++){
			const handle = this.insertHandles[i];
			const a = this.points[i].position;
			const b = this.points[(i + 1) % n].position;
			const mid = a.clone().add(b).multiplyScalar(0.5);

			handle.position.copy(mid);
			handle.quaternion.copy(camera.quaternion);

			const screen = projectToScreen(mid, camera, clientWidth, clientHeight);
			const dx = screen.x - mouse.x;
			const dy = screen.y - mouse.y;
			const near = (dx * dx + dy * dy) <= INSERT_HANDLE_REVEAL_PX * INSERT_HANDLE_REVEAL_PX;

			setInsertHandleNear(handle, near);
			if(near){
				const s = computeScreenSpaceScale(camera, mid, cameraPos, clientWidth, clientHeight, ScreenSpaceSizes.insertHandle);
				handle.scale.set(s * 2, s * 2, 1);
			}
		}
	}

	// Places the discreet "›" chevrons that show which way this line runs
	// (points[0] → points[last]). Recomputed every frame from `this.points`,
	// so it is never stale after any geometry edit (Flyt/Snap/Split/Merge/
	// Trim/Ret ud/Vend retning/…) - reverse() flips them on the very next
	// frame because the vertex order it reads has flipped.
	//
	// Distribution is by on-screen length (see geometry/lineDirection.js): a
	// long line gets a few evenly spaced chevrons (capped), a many-tiny-
	// segments line does not flood, a line only a few pixels long gets none.
	// Constant on-screen size, so zoom never makes them dominate. When
	// `camera` is null (the caller decided this feature must not show a
	// direction at all - Polygon, construction line, …) every marker is
	// hidden.
	updateDirectionIndicators(camera, clientWidth, clientHeight){
		const hideAll = () => { for(const m of this.directionMarkers) m.visible = false; };
		if(!camera || this.points.length < 2){ hideAll(); return; }

		const screenPoints = this.points.map((p) => projectToScreen(p.position, camera, clientWidth, clientHeight));
		const selected = this.selected;
		const plan = planDirectionMarkers(screenPoints, {
			spacingPx: selected ? 95 : 130,
			maxMarkers: selected ? 10 : 6,
		});

		while(this.directionMarkers.length < plan.length){
			const marker = createChevronMarker();
			this.directionMarkers.push(marker);
			this.add(marker);
		}

		const cameraPos = camera.position;
		const targetPx = ScreenSpaceSizes.directionChevron * (selected ? 1.2 : 1);
		const color = selected ? COLOR_SELECTED : COLOR_DEFAULT;

		for(let i = 0; i < this.directionMarkers.length; i++){
			const marker = this.directionMarkers[i];
			if(i >= plan.length){ marker.visible = false; continue; }

			const {segmentIndex, t, angleRad} = plan[i];
			const a = this.points[segmentIndex].position;
			const b = this.points[segmentIndex + 1].position;
			marker.position.set(
				a.x + t * (b.x - a.x),
				a.y + t * (b.y - a.y),
				a.z + t * (b.z - a.z),
			);
			// billboard to the camera, then spin about the view axis to the
			// segment's screen heading (planDirectionMarkers' angle is in the
			// canvas y-down frame; the billboarded mesh's local frame is y-up,
			// hence the negation)
			marker.quaternion.copy(camera.quaternion);
			marker.rotateZ(-angleRad);

			const s = computeScreenSpaceScale(camera, marker.position, cameraPos, clientWidth, clientHeight, targetPx);
			marker.scale.set(s, s, 1);
			marker.material.color.copy(color);
			marker.material.opacity = selected ? 0.95 : 0.5;
			marker.visible = true;
		}
	}

	_wireSphere(sphere){
		const drag = (e) => {
			// en låst visual (fx integrationsbundet, geometri-read-only) kan ikke trækkes - samme flag som skjuler
			// indsæt-håndtag og dobbeltklik-sletning
			if(!this.editable) return;

			const I = Utils.getMousePointCloudIntersection(
				e.drag.end,
				e.viewer.scene.getActiveCamera(),
				e.viewer,
				e.viewer.scene.pointclouds,
				{pickClipped: true});

			if(I){
				const i = this.spheres.indexOf(e.drag.object);
				if(i !== -1){
					// captured BEFORE the mutation - DigitizationModule's shared-
					// node warning (see registerNewFeature()'s onVertexMoved wiring)
					// needs to know where this vertex WAS to check whether it was
					// part of a connection about to be broken, not where it just
					// moved to
					const previousPosition = this.points[i].position.clone();
					if(!this.isDragging) freezeHover(this, i); // under træk er hovermålet frosset til det trukne punkt
						this.isDragging = true; // ryddes ved 'drop' (se nedenfor)
					this.points[i].position.copy(I.location);
					this._sync();
					if(this.onVertexMoved) this.onVertexMoved(i, previousPosition);
				}
			}
		};

		const dblclick = () => {
			if(!this.editable) return;
			const i = this.spheres.indexOf(sphere);
			if(i !== -1) this.removeVertex(i);
		};

		// Deliberately NOT using InputHandler's generic 'select'/'deselect'
		// events + toggleSelection() here: that path assumes selected objects
		// expose a `.boundingBox` (only Volume does) and crashes
		// TransformationTool.update() for a plain vertex sphere. Feature
		// selection is handled entirely within the digitization module instead.
		const click = () => this.dispatchEvent({type: 'feature_clicked', visual: this});

		const mouseover = (e) => {
			e.object.material.emissive.setHex(0x888888);
			e.object.userData.hovered = true;
			bumpLabelRevision();
			hoverEnter("vertex", this, this.spheres.indexOf(e.object), performance.now());
		};
		const mouseleave = (e) => {
			e.object.material.emissive.setHex(0x000000);
			e.object.userData.hovered = false;
			bumpLabelRevision();
			hoverLeave("vertex", this, this.spheres.indexOf(e.object), performance.now());
		};

		// Punktet slippes: træk-tilstanden ophører, og 'vertex_drop' fortæller dem, der først vil regne på den færdige geometri
		// (fx genberegning af Mål areal og volumen), at brugeren er færdig. Udsendes også ved et klik uden bevægelse - lyttere
		// afgør selv, om geometrien faktisk er ændret.
		const drop = () => {
			this.isDragging = false;
			unfreezeHover(performance.now());
			bumpLabelRevision();
			this.dispatchEvent({type: 'vertex_drop'});
		};

		sphere.addEventListener('drag', drag);
		sphere.addEventListener('drop', drop);
		sphere.addEventListener('dblclick', dblclick);
		sphere.addEventListener('click', click);
		sphere.addEventListener('mouseover', mouseover);
		sphere.addEventListener('mouseleave', mouseleave);
	}

	_rebuildEdges(){
		for(const edge of this.edges){ this.remove(edge); disposeEdge(edge); }
		this.edges = [];

		const n = this.points.length;
		if(n < 2) return;

		const segmentCount = this.closed ? n : n - 1;
		for(let i = 0; i < segmentCount; i++){
			const edge = createEdge();
			// .copy(), not direct assignment - see setSelected()'s own doc
			// comment on why aliasing the shared Color singleton must be avoided
			edge.material.color.copy(this.selected ? COLOR_SELECTED : COLOR_DEFAULT);
			this.add(edge);
			this.edges.push(edge);
		}
	}

	_layoutEdges(){
		const n = this.points.length;
		for(let i = 0; i < this.edges.length; i++){
			const a = this.points[i].position;
			const b = this.points[(i + 1) % n].position;
			const edge = this.edges[i];

			edge.position.copy(a);
			edge.geometry.setPositions([0, 0, 0, ...b.clone().sub(a).toArray()]);
			edge.geometry.verticesNeedUpdate = true;
			edge.geometry.computeBoundingSphere();
			edge.computeLineDistances();
		}
	}

	// Mirrors _rebuildEdges() exactly (same segment count/indexing) - one
	// label per edge, torn down and rebuilt whenever the edge count changes.
	// No-op when enableSegmentLabels() was never called (segmentLabelFormat
	// stays null), so every tool that doesn't use this pays nothing extra.
	//
	// Deliberately NO copy icon here (user-requested, unlike every other
	// label type in this codebase) - a segment/distance label sits directly
	// over its own edge's midpoint, exactly where a vertex or the edge
	// itself already is; keeping this one raycast-pickable Mesh out of that
	// crowded spot is one less object competing for hover/click state there.
	_rebuildSegmentLabels(){
		for(const label of this.segmentLabels){ this.remove(label); disposeLabel(label); }
		this.segmentLabels = [];

		if(!this.segmentLabelFormat) return;

		const n = this.points.length;
		if(n < 2) return;

		const segmentCount = this.closed ? n : n - 1;
		for(let i = 0; i < segmentCount; i++){
			const label = createStyledLabel("");
			label.visible = false;
			this.segmentLabels.push(label);
			this.add(label);
		}
	}

	_layoutSegmentLabels(){
		if(!this.segmentLabelFormat) return;

		const n = this.points.length;
		for(let i = 0; i < this.segmentLabels.length; i++){
			const a = this.points[i].position;
			const b = this.points[(i + 1) % n].position;
			const label = this.segmentLabels[i];

			setLabelContent(label, this.segmentLabelFormat(a, b, i) || "");
			label.position.copy(a).add(b).multiplyScalar(0.5);
			setLabelWanted(label, this.labelsVisible);
		}
	}

	// Auto-recomputed variant of setResultLabel() - see enableSummaryLabel().
	// Reuses resultLabel/resultCopyIcon (via setResultLabel itself) rather
	// than a separate label object, so a Målinger tool's summary reads and
	// behaves identically to Rør/Areal og volumen's own result label -
	// same styling, same copy icon, same labelsVisible checkbox semantics.
	_refreshSummaryLabel(){
		if(!this.summaryLabelFormat) return;

		const raw = this.rawCoordinates();
		const lines = this.summaryLabelFormat(raw, this);
		if(!lines){
			if(this.resultLabel) setLabelWanted(this.resultLabel, false);
			return;
		}

		const lastPoint = this.points[this.points.length - 1];
		const anchor = this.summaryLabelAnchor ? this.summaryLabelAnchor(raw, this) : (lastPoint ? lastPoint.position : null);
		if(!anchor) return;

		this.setResultLabel(lines, anchor);
	}

	// XY-projected earcut triangulation, mapped back onto the real 3D vertex
	// positions - same "footprint area" projection as measurementGeometry.js's
	// own polygonArea(), so the shown fill shape always matches the shown
	// area label. Material settings match VolumeQaVisual.js's own cut/fill
	// surface convention (see its own comment): transparent + DoubleSide +
	// depthWrite:true + depthTest:true is the simplest combination that was
	// tested live and held up under orbit/zoom with no flicker.
	// Vertex positions are real-world coordinates (UTM-scale, e.g.
	// 524076.xxx) - uploading those directly into a Float32Array position
	// buffer loses enough precision (Float32 ULP at that magnitude is
	// ~0.03-0.06m) that adjacent/near-coplanar triangles round differently
	// per vertex and z-fight, seen as flickering under camera movement -
	// the exact "flimrende flader" root cause already diagnosed and fixed
	// once for VolumeQaVisual.js (see that file's own header comment) and
	// already avoided elsewhere in THIS file too (_layoutEdges() builds
	// each Line2 from LOCAL [0,0,0]->(b-a) coordinates and puts the large
	// offset on the edge's own `.position`, never in the vertex buffer).
	// Same fix here: triangulate/build the mesh in coordinates local to the
	// first vertex, and carry the large offset via `fillMesh.position`
	// (an Object3D transform, stored as plain float64 JS numbers) instead.
	_rebuildFill(){
		if(this.fillMesh){
			this.remove(this.fillMesh);
			this.fillMesh.geometry.dispose();
			this.fillMesh.material.dispose();
			this.fillMesh = null;
		}

		if(!this.fillColor || this.geometryType !== "Polygon" || this.points.length < 3) return;

		const origin = this.points[0].position;

		const flat = [];
		for(const p of this.points) flat.push(p.position.x - origin.x, p.position.y - origin.y);
		const indices = earcut(flat);
		if(indices.length === 0) return;

		const positions = new Float32Array(this.points.length * 3);
		for(let i = 0; i < this.points.length; i++){
			const pos = this.points[i].position;
			positions[i * 3] = pos.x - origin.x;
			positions[i * 3 + 1] = pos.y - origin.y;
			positions[i * 3 + 2] = pos.z - origin.z;
		}

		const geometry = new THREE.BufferGeometry();
		geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
		geometry.setIndex(indices);

		const material = new THREE.MeshBasicMaterial({
			color: this.fillColor, side: THREE.DoubleSide, transparent: true, opacity: this.fillOpacity,
			depthWrite: true, depthTest: true,
		});

		this.fillMesh = new THREE.Mesh(geometry, material);
		this.fillMesh.position.copy(origin);
		this.add(this.fillMesh);
	}

	_refreshVertexLabel(index){
		const label = this.vertexLabels[index];
		const point = this.points[index];
		if(!label || !point) return;

		// Indholdstrappe (LABEL-STRATEGI.md 12.3): kote -> X/Y/Z -> rolle + X/Y/Z. Objektets navn og beskrivelse gentages IKKE pr. punkt (de
		// ligger i resultatlabelen, hover-peek og Registreringer); kun et rollenavn (fx "Midtpunkt 1") eller navnet på et enkeltpunkt tages med
		// i det fulde trin.
		const role = (this.vertexRoleNames && this.vertexRoleNames[index]) || (this.points.length === 1 ? this.name : null);
		const xyz = formatCoordinateLine(point.position);
		const zLine = xyz.slice(xyz.lastIndexOf("\n") + 1);
		const head = role ? [role].concat(this.points.length === 1 && this.description ? [this.description] : []) : null;
		label.userData.pvIconPad = COPY_ICON_PAD;
		setLabelLadder(label, [null, zLine, xyz, head ? head.join("\n") + "\n" + xyz : null]);
		label.position.copy(point.position);
		setLabelWanted(label, this.labelsVisible && !this.vertexLabelsSuppressed);
	}

	_refreshComputedPoint(){
		if(!this.computeFn || !this.computedMarker) return;

		if(this.points.length < 2){
			this.computedMarker.visible = false;
			this.computedLabel.visible = false;
			return;
		}

		const result = this.computeFn(this.rawCoordinates());
		if(!result){
			this.computedMarker.visible = false;
			this.computedLabel.visible = false;
			return;
		}

		const pos = new THREE.Vector3(result[0], result[1], result[2]);
		this.computedMarker.position.copy(pos);
		this.computedMarker.visible = true;

		const lines = [this.computedPointName, this.description, formatCoordinateLine(pos)];
		this.computedLabel.setText(lines.join("\n"));
		this.computedLabel.position.copy(pos);
		this.computedLabel.visible = this.labelsVisible;
	}

	_sync(){
		bumpLabelRevision();
		for(let i = 0; i < this.points.length; i++){
			this.spheres[i].position.copy(this.points[i].position);
			this._refreshVertexLabel(i);
		}
		this._refreshComputedPoint();
		this._layoutEdges();
		this._layoutSegmentLabels();
		this._refreshSummaryLabel();
		this._rebuildFill();

		this.dispatchEvent({type: "geometry_changed"});
	}

	// raw [x,y,z] arrays of the captured points, full precision, no post-processing
	rawCoordinates(){
		return this.points.map(p => [p.position.x, p.position.y, p.position.z]);
	}

	// GeoJSON-shaped coordinates for this visual's own geometry type (Point:
	// [x,y,z], LineString: [[x,y,z],...], Polygon: [[[x,y,z],... ,first]])
	toCoordinates(){
		const coords = this.rawCoordinates();
		if(this.geometryType === "Point") return coords[0];
		if(this.geometryType === "Polygon") return [[...coords, coords[0]]];
		return coords;
	}

	updateResolution(width, height){
		for(const edge of this.edges){
			edge.material.resolution.set(width, height);
		}
	}

	updateSphereScale(camera, clientWidth, clientHeight){
		const cameraPos = camera.position;

		for(const sphere of this.spheres){
			// per-role constant on-screen size (see vertexSemantics.js) - the
			// ●/■/◎ size hierarchy. Falls back to the plain-vertex size for a
			// sphere whose role has not been assigned yet.
			const role = sphere.userData.vertexRole || VERTEX_ROLE.VERTEX;
			const style = vertexRoleStyle(role);
			const targetPixels = sphere.userData.hovered ? style.hoverPixels : style.pixels;
			const scale = computeScreenSpaceScale(
				camera, sphere.getWorldPosition(new THREE.Vector3()), cameraPos,
				clientWidth, clientHeight, targetPixels);
			sphere.scale.set(scale, scale, scale);

			// forms that only read correctly face-on (cube square, torus ring)
			// track the camera; a sphere is left alone (rotation-invariant)
			if(BILLBOARDED_ROLES.has(role)) sphere.quaternion.copy(camera.quaternion);
		}

		if(this.computedMarker && this.computedMarker.visible){
			const scale = computeScreenSpaceScale(
				camera, this.computedMarker.getWorldPosition(new THREE.Vector3()), cameraPos,
				clientWidth, clientHeight, ScreenSpaceSizes.computedPoint);
			this.computedMarker.scale.set(scale, scale, scale);
		}
	}

	// axes: {right, up} (kameraets akser i verden) fra LabelManager - lægger labelens skærm-forskydning oven på ankeret
	updateLabelScale(camera, clientWidth, clientHeight, axes){
		const labels = [...this.vertexLabels, ...this.segmentLabels];
		if(this.computedLabel) labels.push(this.computedLabel);
		if(this.resultLabel) labels.push(this.resultLabel);

		for(const label of labels){
			if(!label.visible) continue;

			const scale = computeScreenSpaceScale(
				camera, label.getWorldPosition(new THREE.Vector3()), camera.position,
				clientWidth, clientHeight, ScreenSpaceSizes.label);
			label.scale.set(scale, scale, scale);
			if(axes) applyLabelOffset(label, axes, camera);
		}

		// hjælpelabels (Højde, Vinkel, Azimut, Cirkel ...) skaleres af deres egen ejer, men får LabelManagers forskydning her
		const aux = axes && this.userData.auxiliaryVisual;
		if(aux && typeof aux.auxLabels === "function"){
			for(const label of aux.auxLabels()){
				if(label.visible) applyLabelOffset(label, axes, camera);
			}
		}
	}

	// Tucks each copy-icon into its label's own top-right corner (in world
	// space, along the camera's local "right"/"up" axes, so it stays glued to
	// that corner from any viewing angle) and keeps it at a constant on-screen
	// size. Must run AFTER updateLabelScale() in the same frame - it reads
	// each label's already-updated `label.scale.x` (its current, per-frame
	// on-screen-equivalent scale factor) together with the label's own
	// unscaled canvas width/height (label.sprite.scale.x/y) to find that
	// corner exactly, without needing to duplicate or guess at text-size math.
	updateCopyIcons(camera, clientWidth, clientHeight){
		for(let i = 0; i < this.vertexLabels.length; i++){
			positionCopyIconAtLabelCorner(this.copyIcons[i], this.vertexLabels[i], camera, clientWidth, clientHeight);
		}

		positionCopyIconAtLabelCorner(this.computedCopyIcon, this.computedLabel, camera, clientWidth, clientHeight);
		positionCopyIconAtLabelCorner(this.resultCopyIcon, this.resultLabel, camera, clientWidth, clientHeight);
	}

	// Full GPU-resource teardown for when this ENTIRE visual is discarded
	// (a feature deleted outright - see DigitizationModule.js's own
	// removeFeature()), as opposed to the partial rebuilds above (which
	// already dispose exactly what THEY replace). Covers every object this
	// class can own across its lifetime, including the ones only ever
	// created once and kept for as long as the visual exists (computedMarker/
	// Label/CopyIcon, resultLabel/CopyIcon) which the partial-rebuild paths
	// above never touch. Does NOT call `this.remove(...)` for anything -
	// the caller removes this whole Object3D from the scene graph in one
	// step; this method's only job is releasing the GPU-side resources
	// scene-graph removal alone does not free.
	dispose(){
		// vertex-marker geometry is now the shared, module-level ROLE_GEOMETRY
		// set (see vertexSemantics.js) - never disposed per-visual; only each
		// sphere's own material is this visual's to free.
		for(const sphere of this.spheres) disposeSphere(sphere);
		for(const label of this.vertexLabels) disposeLabel(label);
		for(const icon of this.copyIcons) disposeCopyIcon(icon);
		for(const edge of this.edges) disposeEdge(edge);
		for(const label of this.segmentLabels) disposeLabel(label);
		for(const handle of this.insertHandles) disposeInsertHandle(handle);
		for(const marker of this.directionMarkers) disposeChevron(marker);

		if(this.computedMarker){
			this.computedMarker.geometry.dispose();
			this.computedMarker.material.dispose();
		}
		disposeLabel(this.computedLabel);
		disposeCopyIcon(this.computedCopyIcon);

		disposeLabel(this.resultLabel);
		disposeCopyIcon(this.resultCopyIcon);

		if(this.fillMesh){
			this.fillMesh.geometry.dispose();
			this.fillMesh.material.dispose();
		}

		if(this.volumeQaGroup) disposeObjectTree(this.volumeQaGroup);

		// same generic, duck-typed hook setLabelsVisible() already uses (see
		// its own comment) - an attached auxiliary (HeightAuxiliaryVisual.js,
		// CrossSectionMarkersVisual.js, ...) owns its own GPU resources and is
		// responsible for freeing them here, without this class needing to
		// know what kind of object it is
		if(this.userData.auxiliaryVisual && this.userData.auxiliaryVisual.dispose){
			this.userData.auxiliaryVisual.dispose();
		}
	}
};
